import Drawer, { DrawerSize } from "@galaxy-io/dls/drawer/Drawer";
import Flex, { FlexDirection } from "@galaxy-io/dls/layout/Flex";
import ConfirmDialog from "@galaxy-io/dls/modal/ConfirmDialog";
import Modal, { ModalSize } from "@galaxy-io/dls/modal/Modal";
import { type Icon as PhosphorIcon, SidebarSimpleIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { BaseHeader } from "@/components/workspace/base-header";

/** DLS owns the overlay stack, trapping, Escape, and return to the opener. */
export function SurfaceDialog({
  open,
  onClose,
  title,
  children,
  footer,
  drawer = false,
  dirty = false,
  busy = false,
  restoreFocus,
  headerActions,
  headerTitle,
  headerDescription,
  headerLeading,
  headerIcon,
  flush = false,
  width = 540,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  drawer?: boolean;
  dirty?: boolean;
  busy?: boolean;
  restoreFocus?: () => void;
  headerActions?: ReactNode;
  headerTitle?: string;
  headerDescription?: ReactNode;
  headerLeading?: ReactNode;
  headerIcon?: PhosphorIcon;
  flush?: boolean;
  width?: number;
}) {
  const [discard, setDiscard] = useState(false);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      setDiscard(false);
      // URL-driven drawers may outlive their original row; callers provide a fallback.
      if (restoreFocus) {
        const timer = setTimeout(restoreFocus, 160);
        wasOpen.current = open;
        return () => clearTimeout(timer);
      }
    }
    wasOpen.current = open;
  }, [open, restoreFocus]);
  const changeOpen = (next: boolean) => {
    if (next || busy) return;
    if (dirty) setDiscard(true);
    else onClose();
  };
  const header = (
    <BaseHeader
      title={headerTitle || title}
      description={typeof headerDescription === "string" ? undefined : headerDescription}
      leading={headerLeading}
      level={2}
      actions={drawer ? undefined : headerActions}
    />
  );
  const body = (
    <Flex direction={FlexDirection.COLUMN} gap={flush && !drawer ? 0 : 16} minHeight={0}>
      {children}
    </Flex>
  );
  const common = {
    isOpen: open,
    onOpenChange: changeOpen,
    isDismissable: !busy,
    ariaLabel: title,
    header:
      headerLeading ||
      (!drawer && headerActions) ||
      (headerDescription && typeof headerDescription !== "string")
        ? header
        : headerTitle || title,
    subheader: typeof headerDescription === "string" ? headerDescription : undefined,
    icon: headerIcon ?? (drawer && !headerLeading ? SidebarSimpleIcon : undefined),
    footer,
  };
  return (
    <>
      {drawer ? (
        <Drawer {...common} actions={headerActions} size={DrawerSize.LARGE}>
          {body}
        </Drawer>
      ) : (
        <Modal
          {...common}
          size={width > 720 ? ModalSize.X_LARGE : width > 560 ? ModalSize.LARGE : ModalSize.MEDIUM}
        >
          {body}
        </Modal>
      )}
      <ConfirmDialog
        isOpen={open && discard}
        onOpenChange={setDiscard}
        header="Discard changes?"
        description="Your changes haven’t been saved."
        label="Discard changes"
        isDestructive
        onConfirm={() => {
          setDiscard(false);
          onClose();
        }}
      />
    </>
  );
}
